import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Group, IconInfo, Typography, Wrapper } from '@screentone/core';

import styles from './ModuleRibbonIndicator.module.scss';

interface RibbonTab {
  title: string;
}

interface RibbonData {
  sectionTitle?: string;
  tabs?: RibbonTab[];
}

interface ModuleRibbonIndicatorProps {
  ribbonUrl: string;
  withBackground?: boolean;
}

export const ModuleRibbonIndicator = ({ ribbonUrl, withBackground = false }: ModuleRibbonIndicatorProps) => {
  const testId = 'module-ribbon-indicator';
  const [ribbon, setRibbon] = useState<RibbonData | undefined>(undefined);
  const [fetchError, setFetchError] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);
  const renderedItemRef = useRef<HTMLDivElement>(null);
  const measuredItemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    setFetchError(false);
    fetch(ribbonUrl)
      .then((res) => res.json() as Promise<RibbonData>)
      .then((data) => {
        if (!cancelled) {
          setRibbon(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFetchError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [ribbonUrl]);

  const tabs = ribbon?.tabs ?? [];

  const recalculate = useCallback(() => {
    const visible = renderedItemRef.current;
    const measure = measuredItemRef.current;
    if (!visible || !measure || tabs.length === 0) {
      return;
    }

    const containerWidth = visible.getBoundingClientRect().width;
    if (containerWidth === 0) {
      return;
    }

    const gapPx = parseFloat(getComputedStyle(measure).gap) || 0;
    const tabOffset = ribbon?.sectionTitle ? 1 : 0;
    const moreEl = measure.children[measure.children.length - 1] as HTMLElement | undefined;
    const moreWidth = moreEl ? moreEl.offsetWidth : 0;
    const cutoff = containerWidth - moreWidth - gapPx;

    let count = 0;
    for (let i = 0; i < tabs.length; i++) {
      const child = measure.children[tabOffset + i] as HTMLElement | undefined;
      if (!child) {
        break;
      }
      if (child.offsetLeft + child.offsetWidth > cutoff) {
        break;
      }
      count++;
    }

    setVisibleCount(count === tabs.length ? null : count);
  }, [tabs.length, ribbon?.sectionTitle]);

  useLayoutEffect(() => {
    recalculate();
  }, [recalculate]);

  useEffect(() => {
    const visible = renderedItemRef.current;
    const observer = new ResizeObserver(recalculate);

    if (visible) {
      observer.observe(visible);
    }

    return () => {
      observer.disconnect();
    };
  }, [recalculate]);

  const hiddenCount = useMemo(
    () => (visibleCount !== null ? tabs.length - visibleCount : 0),
    [visibleCount, tabs.length]
  );
  const displayTabs = visibleCount !== null ? tabs.slice(0, visibleCount) : tabs;

  const ribbonLayout = useCallback(
    (tabList: RibbonTab[]) => (
      <>
        {ribbon?.sectionTitle && (
          <Typography size="sm" weight="bold" color="asphalt" className={styles.sectionTitle}>
            {ribbon.sectionTitle}
          </Typography>
        )}
        {tabList.map((tab, i) => (
          <Typography key={i} size="sm" color="asphalt" className={styles.tab}>
            {tab.title}
          </Typography>
        ))}
        {hiddenCount > 0 && (
          <Typography size="sm" color="asphalt" className={styles.more}>
            +{hiddenCount} more
          </Typography>
        )}
      </>
    ),
    [ribbon, hiddenCount]
  );

  if (fetchError) {
    return (
      <Wrapper margin={{ bottom: 'xs' }} data-testid={`${testId}-error`}>
        <IconInfo className={styles.errorIcon} />
        <Typography size="sm" className={styles.errorIcon}>
          Ribbon failed to load.
        </Typography>
      </Wrapper>
    );
  }

  if (!ribbon) {
    return null;
  }

  return (
    <Wrapper
      className={withBackground ? styles.withBackground : undefined}
      margin={withBackground ? undefined : { bottom: 'xs' }}
      data-testid={testId}
    >
      <Group ref={measuredItemRef} gap="md" valign="center" wrap={false} className={styles.measure} aria-hidden="true">
        {ribbonLayout(tabs)}
      </Group>
      <Group
        ref={renderedItemRef}
        gap="md"
        valign="center"
        wrap={false}
        className={withBackground ? styles.contentRow : styles.ribbonIndicator}
        data-testid={`${testId}-content`}
      >
        {ribbonLayout(displayTabs)}
      </Group>
    </Wrapper>
  );
};
